import { getHolidayName } from '../utils/holidays';
import { CalendarIcon } from './ui/Icons';

interface HolidaySettingsProps {
  showHolidays: boolean;
  onToggleHolidays: (value: boolean) => void;
}

export function HolidaySettings({ showHolidays, onToggleHolidays }: HolidaySettingsProps) {
  // Get current year and next year
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear + 1];
  
  // Get all holidays for display
  const holidays: Array<{ date: string; name: string }> = [];
  
  years.forEach(year => {
    // Check all days in the year for holidays
    const startDate = new Date(year, 0, 1);
    const endDate = new Date(year, 11, 31);
    
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      const holidayName = getHolidayName(dateStr);
      if (holidayName) {
        holidays.push({ date: dateStr, name: holidayName });
      }
    }
  });

  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-xs font-semibold text-gray-100 flex items-center gap-2">
          <CalendarIcon />
          Japanese Holiday Settings
        </h3>
        <p className="text-xs text-gray-400 mt-1">
          Configure how Japanese holidays are displayed in the dashboard
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between p-2 bg-gray-800/50 rounded-lg border border-gray-700">
          <div>
            <h4 className="text-sm font-medium text-gray-200">Show Holiday Overlays</h4>
            <p className="text-xs text-gray-400 mt-1">
              Display holiday and weekend overlays on usage charts
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={showHolidays}
              onChange={(e) => onToggleHolidays(e.target.checked)}
            />
            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-200">Holiday Calendar</h4>
          <div className="bg-gray-800/30 rounded-lg border border-gray-700 max-h-64 overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 px-2 py-1">
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded"></div>
                  <span className="text-gray-400">Holidays</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-gray-600 rounded"></div>
                  <span className="text-gray-400">Weekends</span>
                </div>
              </div>
            </div>
            
            {years.map(year => {
              const yearHolidays = holidays.filter(h => h.date.startsWith(year.toString()));
              if (yearHolidays.length === 0) return null;
              
              return (
                <div key={year} className="p-2">
                  <h5 className="text-xs font-semibold text-gray-300 mb-1.5">{year}</h5>
                  <div className="grid grid-cols-1 gap-1">
                    {yearHolidays.map(holiday => {
                      const date = new Date(holiday.date);
                      const dayOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
                      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                      
                      return (
                        <div
                          key={holiday.date}
                          className={`flex items-center justify-between px-2 py-1 rounded text-xs ${
                            isWeekend 
                              ? 'bg-gray-700/50 text-gray-300' 
                              : 'bg-amber-900/20 text-amber-200'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-gray-400">
                              {holiday.date} ({dayOfWeek})
                            </span>
                            <span className="font-medium">{holiday.name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}